import {
	Grid,
	Box,
	Stack,
	Typography,
	Checkbox,
	IconButton,
	Popper,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as GroupIcon } from 'common/assets/images/group.svg';
import AccordionComp from 'common/components/Accordion/AccordionComp';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import SelectableHOC from 'common/components/SelectableHOC/SelectableHOC';
import SideDrawer from 'common/components/SideDrawer/SideDrawer';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { MODULE_NAME, TASKTYPE } from 'common/constants';
import {
	APPLICABILITY_STATUS,
	PROGRESS_BAR_ELEMENTS,
	STATUS_UPDATE_TYPE,
} from 'common/constants/CitationDetails';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import {
	AXIOS_NETWORK_ERR_CODE,
	submissionFailedMsg,
} from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setRegInvententoryAddToGroupDrawer,
	setRegInvententoryCreateDrawer,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { urlWithTenant } from 'common/utils/utilFunctions';

import RegulatoryInventoryAddToGroup from 'features/RegulationInventory/Components/AddToGroup/RegulationInventoryAddToGroup';
import CitationDescription from 'features/RegulationInventory/Components/CitationDescription/CitationDescription';
import RegulationBreadcrumbs from 'features/RegulationInventory/Components/RegulationBreadcrumb/RegulationBreadcrumbs';
import RegulationDetailsHeader from 'features/RegulationInventory/Components/RegulationHeader/RegulationDetailsHeader';

import {
	getCitationDetails,
	postGroupAssessment,
	putGroupAssessment,
} from '../api';
import {
	ICitationDetailsElements,
	ICitationGroup,
	ICitationsUpdateRequestParams,
	ISelectedCitationGroups,
	ISubPartCitationDetails,
} from '../api/type-def';
import RegulatoryInventoryAddGroup from '../RegulatoryInventoryAddGroup';

import CitationGroupList from './CitationGroupList';
import {
	checkAnyChildrenSelected,
	checkAllChildrenSelected,
	getGroupsPerRegulation,
	getGroupAssessmentAPIParams,
	getGroupingBreadCrumbs,
	unCheckAllCitationForRegulation,
	checkAllCitationForRegulation,
} from './RegulationGroupUtil';

import styles from './GroupAssessmentDetails.module.scss';

const { checkboxCheckedcolor, checkboxUncheckedcolor } = styles;

const { REQUIREMENT_INVENTORY_NAME } = MODULE_NAME;

const { WORKFLOW } = TASKTYPE;

const { SAVE, REVIEW } = STATUS_UPDATE_TYPE;

const { OK, CREATED } = HTTP_STATUS_CODES;

const { GROUPING_ASSESSMENT, APPLICABILITY_ASSESSMENT, GROUPING_REVIEW } =
	PROGRESS_BAR_ELEMENTS;

const { APPLICABLE, INFORMATIONAL, NOTAPPLICABLE } = APPLICABILITY_STATUS;

const { regulationInventory, overView } = rrcpDashboardRoutes;

interface IGroupAssessmentDetailsProps {
	storageWrapper: any;
}
const GroupAssessmentDetails = (props: IGroupAssessmentDetailsProps) => {
	const { storageWrapper } = props;

	const [subpart, setSubpart] = useState<ISubPartCitationDetails>(
		{} as ISubPartCitationDetails,
	);
	const [selectedAccordians, setSelectedAccordians] = useState<
		ICitationDetailsElements[]
	>([]);
	const [checkedCitations, setCheckedCitations] = useState<
		ISelectedCitationGroups[]
	>([]);
	// The below state is to maintain groups at accordion level
	const [originalRegulationGroups, setOriginalRegulationGroups] = useState<
		ISelectedCitationGroups[]
	>([]);
	const [regulationGroups, setRegulationGroups] = useState<
		ISelectedCitationGroups[]
	>([]);
	const [selectAllChecked, setSelectAllChecked] = useState(false);

	const [loading, setIsLoading] = useState<boolean>(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedGroup, setSelectedGroup] =
		useState<ICitationDetailsElements>();
	const dispatch = useDispatch();

	const addGroupDrawerState = useSelector(
		(state: RootState) => state.common.regInvententoryCreateDrawer,
	);
	const addToGroupDrawerState = useSelector(
		(state: RootState) => state.common.regInvententoryAddToGroupDrawer,
	);
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const open = Boolean(anchorEl);
	const { subPartId } = useParams();
	const navigate = useNavigate();
	const taskDetails: ICitationsUpdateRequestParams = {
		taskId: subpart?.taskId,
		taskStatus: subpart?.taskStatus,
		regulationRelationshipId: subpart?.applicabilityAssessment?.id,
		sectionId: subpart?.applicabilityAssessment?.id,
		sectionName: subpart?.applicabilityAssessment?.title,
		assessmentUpdate: false,
		actionType: SAVE,
		taskType: WORKFLOW,
		moduleName: REQUIREMENT_INVENTORY_NAME,
	};
	const editPermission =
		subpart.taskStatus === GROUPING_ASSESSMENT &&
		loggedInUser?.userId === subpart?.assignedUserId;

	useEffect(() => {
		fetchSubpartGroupData();
	}, []);

	const getApplicableRecords = (respData: ISubPartCitationDetails) => {
		const applicableData = respData;
		if (
			respData.applicabilityAssessment.children.length &&
			respData.applicabilityAssessment.children.length > 0 &&
			respData.taskStatus === APPLICABILITY_ASSESSMENT
		) {
			const childNode = respData?.applicabilityAssessment?.children?.filter(
				({ applicabilityStatus }: ICitationDetailsElements) =>
					applicabilityStatus === APPLICABLE ||
					applicabilityStatus === INFORMATIONAL,
			);
			applicableData.applicabilityAssessment.children = childNode;
		}
		return applicableData;
	};

	const fetchSubpartGroupData = async () => {
		setIsLoading(true);
		const resp: any = await getCitationDetails(subPartId || '');
		if (resp?.statusCode === OK) {
			const respData = resp?.responseBody;
			const applicableData = getApplicableRecords(respData);
			setIsLoading(false);
			setSubpart(applicableData);
			const allCitationGroup: ISelectedCitationGroups[] = [];
			// Get all groups and store in original and state.
			applicableData?.applicabilityAssessment?.children?.forEach(
				(res: ICitationDetailsElements) => {
					const citationGroups: ISelectedCitationGroups[] = [];
					getGroupsPerRegulation(res, res.id, citationGroups);
					allCitationGroup.push(...citationGroups);
				},
			);
			setOriginalRegulationGroups(allCitationGroup);
			setRegulationGroups(allCitationGroup);
		} else {
			setIsLoading(false);
		}
	};

	const checkAllCitation = (
		node: ICitationDetailsElements,
		rootNode?: ICitationDetailsElements | null,
		checkedCitations: ISelectedCitationGroups[] = [],
	) => {
		const uniqArray = checkAllCitationForRegulation(
			checkedCitations,
			node,
			rootNode,
		);
		return uniqArray;
	};

	const unCheckAllCitation = (
		node: ICitationDetailsElements,
		rootNode?: ICitationDetailsElements | null,
		checkedCitations: ISelectedCitationGroups[] = [],
	) => {
		const newCitationArray = unCheckAllCitationForRegulation(
			checkedCitations,
			node,
			rootNode,
		);
		return newCitationArray;
	};

	const handleCheckBox = (
		event: React.SyntheticEvent,
		node: ICitationDetailsElements,
		rootNode?: ICitationDetailsElements | null,
	) => {
		event?.stopPropagation();
		if ((event?.target as HTMLInputElement).checked) {
			const checkedArray = checkAllCitation(node, rootNode, checkedCitations);
			setCheckedCitations(checkedArray);
			if (
				checkAllChildrenSelected(checkedArray, subpart?.applicabilityAssessment)
			) {
				setSelectAllChecked(true);
			}
		} else {
			const newCitationArray = unCheckAllCitation(
				node,
				rootNode,
				checkedCitations,
			);
			if (
				!checkAllChildrenSelected(
					newCitationArray,
					subpart?.applicabilityAssessment,
				)
			) {
				setSelectAllChecked(false);
			}
			setCheckedCitations(() => newCitationArray);
		}

		if (selectedGroup) {
			setAnchorEl(null);
			setSelectedGroup(undefined);
		}
	};

	const handleSelectAllCheckBox = (event: React.SyntheticEvent) => {
		setSelectAllChecked((event.target as HTMLInputElement).checked);

		const uniqArray: ISelectedCitationGroups[] = [];
		subpart?.applicabilityAssessment?.children?.map((res) => {
			if ((event.target as HTMLInputElement).checked) {
				const checkedArray = checkAllCitation(res, null);
				uniqArray.push(...checkedArray);
			} else {
				const uncheckedArray = unCheckAllCitation(res, null);
				uniqArray.push(...uncheckedArray);
			}
			return null;
		});
		if ((event.target as HTMLInputElement).checked) {
			setCheckedCitations(() => uniqArray);
		} else {
			setCheckedCitations([]);
		}
		if (selectedGroup) {
			setAnchorEl(null);
			setSelectedGroup(undefined);
		}
	};

	const handleGroupClick = (node: ICitationDetailsElements) => (event: any) => {
		event.stopPropagation();
		setAnchorEl(node === selectedGroup ? null : event.currentTarget);
		setSelectedGroup(node === selectedGroup ? undefined : node);
	};

	const handleAccordianChange = (
		val: ICitationDetailsElements,
		event: React.SyntheticEvent,
	) => {
		event.stopPropagation();
		if (selectedAccordians.includes(val)) {
			setSelectedAccordians((res) => res.filter((elem) => elem !== val));
		} else {
			setSelectedAccordians([...selectedAccordians, val]);
		}
	};
	const onHandleCancel = () => {
		setCheckedCitations([]);
		setSelectAllChecked(false);
	};

	const handleCitationGroupClose = () => {
		setAnchorEl(null);
		setSelectedGroup(undefined);
	};
	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const onAddToGroupClick = useCallback(() => {
		dispatch(setRegInvententoryAddToGroupDrawer(true));
	}, [setRegInvententoryAddToGroupDrawer]);

	const onCreateGroupClick = useCallback(() => {
		dispatch(setRegInvententoryCreateDrawer(true));
	}, [setRegInvententoryCreateDrawer]);

	const regulationGroupDelete = (group: ICitationGroup, parentId: string) => {
		const newRegulationGroup = regulationGroups.map(
			(currentNode: ISelectedCitationGroups) => {
				if (
					currentNode.parentId === parentId &&
					currentNode.group.findIndex((el) => el.groupId === group.groupId) >= 0
				) {
					const newObj: ISelectedCitationGroups = {
						...currentNode,
						group: currentNode.group.filter(
							(ele) => ele.groupId !== group.groupId,
						),
					};
					return newObj;
				}
				return currentNode;
			},
		);
		setRegulationGroups(() => [...newRegulationGroup]);
		displayToast(
			'The associated group deleted successfully, please click Save button',
			'success',
		);
	};

	const displayToast = (msg: string, type: string) => {
		dispatch(
			setToastMessage({
				message: msg,
				severity: type,
				isToastOpen: true,
			}),
		);
	};

	const handleSubmitClick = async () => {
		const { postArgument, putArgument } = getGroupAssessmentAPIParams(
			regulationGroups,
			taskDetails,
			originalRegulationGroups,
			GROUPING_REVIEW,
		);
		let response;
		if (postArgument?.grouping?.length) {
			postArgument.taskDetails.actionType = REVIEW;
			response = await postGroupAssessment(postArgument);
		}
		if (putArgument?.grouping?.length) {
			putArgument.taskDetails.actionType = REVIEW;
			response = await putGroupAssessment(putArgument);
		}
		if ((response?.error as any)?.code === AXIOS_NETWORK_ERR_CODE) {
			displayToast(submissionFailedMsg, 'error');
			return;
		}
		if (displayResponseMessage(response)) {
			navigate(urlWithTenant(`/${regulationInventory}/${overView}`));
		}
	};

	const handleCancelClick = useCallback(() => {
		setRegulationGroups([...originalRegulationGroups]);
		setAnchorEl(null);
		setSelectedGroup(undefined);
		displayToast('The associated group restored successfully', 'success');
	}, [originalRegulationGroups, regulationGroups, selectedGroup, anchorEl]);

	const displayResponseMessage = (response: any) => {
		if (response?.statusCode === OK || response?.statusCode === CREATED) {
			displayToast('The details saved successfully', 'success');
			return true;
		}
		if (response?.errorBody || response?.error) {
			const { errorBody } = response;
			const responseError: any = response.error;
			const errorMsg =
				(errorBody?.errorMessage as string) ||
				(responseError?.message as string);
			displayToast(errorMsg, 'error');
		}
		return false;
	};
	const saveGroupChanges = async () => {
		let response;
		const { postArgument, putArgument } = getGroupAssessmentAPIParams(
			regulationGroups,
			taskDetails,
			originalRegulationGroups,
		);
		if (postArgument?.grouping?.length) {
			postArgument.taskDetails.actionType = SAVE;
			response = await postGroupAssessment(postArgument);
		}
		if (putArgument?.grouping?.length) {
			putArgument.taskDetails.actionType = SAVE;
			response = await putGroupAssessment(putArgument);
		}
		displayResponseMessage(response);
	};

	const handleSaveClick = async () => {
		await saveGroupChanges();
		await fetchSubpartGroupData();
	};

	const onAddHandler = (addedGroups: ICitationGroup[]) => {
		const newGroupsList = checkedCitations.map(
			(currentNode: ISelectedCitationGroups) => ({
				...currentNode,
				group: addedGroups,
			}),
		);
		const newRegulationGroups = regulationGroups.map((citation) => {
			const addedGroups = newGroupsList.find(
				(ele) => ele.citationId === citation.citationId,
			);
			if (addedGroups) {
				const uniqArray = Array.from(
					new Set([...citation.group, ...addedGroups.group]),
				);
				return {
					...citation,
					group: uniqArray,
				};
			}
			return citation;
		});

		const combinedArray = [...newRegulationGroups, ...newGroupsList];
		setRegulationGroups([...combinedArray]);
		dispatch(setRegInvententoryAddToGroupDrawer(false));
		setCheckedCitations([]);
		displayToast(
			'The group added successfully, please click Save button',
			'success',
		);
	};

	const breadCrumbText = useSelector(
		(state: RootState) => state.common.requirementInventoryBreadcrumbText,
	);

	return (
		<Grid container spacing={2}>
			{loading ? (
				<Loader show />
			) : (
				<>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12} pt={0}>
						<RegulationBreadcrumbs
							items={getGroupingBreadCrumbs(
								breadCrumbText,
								'',
								subpart?.applicabilityAssessment?.title,
							)}
							storageWrapper={storageWrapper}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						{subpart.applicabilityAssessment && (
							<RegulationDetailsHeader
								taskId={subpart.taskId}
								status={subpart.taskStatus}
								assignedUserName={subpart.assignedUserName}
								jurisdiction={subpart.jurisdiction}
								reviewByDate={subpart.reviewByDate}
								title={subpart.applicabilityAssessment.title}
							/>
						)}
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						<SelectableHOC
							count={checkedCitations.length}
							visible={checkedCitations.length > 0}
							styleClass={styles['selectable-wrapper']}
							element={
								<Box className={styles['selectable-wrapper-flex']}>
									<ButtonComp
										btnType="secondary"
										clickHandler={onAddToGroupClick}
										data-testid="bulk-apply-btn"
									>
										Add to Group
									</ButtonComp>
									<ButtonComp
										btnType="primary"
										clickHandler={onCreateGroupClick}
										compClass={styles['create-group-button']}
										data-testid="create-group-btn"
									>
										Create Group
									</ButtonComp>
								</Box>
							}
							handleCancelChange={onHandleCancel}
						>
							<Stack className={styles['citaion-details-wrapper']} gap="4px">
								{(subpart?.applicabilityAssessment?.children?.length || 0) >
									1 &&
									editPermission && (
										<Grid
											item
											xs={12}
											sm={12}
											md={12}
											lg={12}
											xl={12}
											display="flex"
										>
											<Checkbox
												data-testid="inventory-checkboxAll"
												size="small"
												checked={selectAllChecked}
												onChange={handleSelectAllCheckBox}
												className={styles['select-all-checkbox']}
												sx={{
													color: checkboxUncheckedcolor,
													'&.Mui-checked': { color: checkboxCheckedcolor },
													'&.MuiCheckbox-indeterminate': {
														color: checkboxCheckedcolor,
													},
												}}
											/>
											<Typography className={styles['select-all-text']}>
												Select All
											</Typography>
										</Grid>
									)}
								{subpart?.applicabilityAssessment?.children?.map(
									(res, index) => {
										const selectedNode = checkedCitations.some(
											(node) => node.citationId === res.id,
										);
										return (
											<AccordionComp
												className={styles['accordion-margin-btm']}
												expanded={selectedAccordians.includes(res)}
												onChange={(event) => handleAccordianChange(res, event)}
												isLeftAccordian
												key={res.id}
												summary={
													<Stack
														direction="row"
														justifyContent="space-between"
														width="100%"
													>
														<Stack
															direction="row"
															alignItems="center"
															width="100%"
														>
															{editPermission && (
																<Checkbox
																	size="small"
																	checked={selectedNode}
																	data-testid={`inventory-checkbox${index}`}
																	indeterminate={
																		checkAnyChildrenSelected(
																			checkedCitations,
																			res,
																		) &&
																		!checkAllChildrenSelected(
																			checkedCitations,
																			res,
																		)
																	}
																	onChange={(e) => handleCheckBox(e, res, null)}
																	sx={{
																		color: checkboxUncheckedcolor,
																		'&.Mui-checked': {
																			color: checkboxCheckedcolor,
																		},
																		'&.MuiCheckbox-indeterminate': {
																			color: checkboxCheckedcolor,
																		},
																	}}
																/>
															)}
															<Typography
																className={styles['inventory-citation-heading']}
																data-testid="inventory-desc"
																onClick={(event) =>
																	handleAccordianChange(res, event)
																}
															>
																{res.description}
															</Typography>
															{res.applicabilityStatus === NOTAPPLICABLE ? (
																<Typography
																	className={styles['applicablity-text']}
																>
																	{res.applicabilityStatus}
																</Typography>
															) : (
																<Box className={styles['group-icon-wrapper']}>
																	<IconButton
																		size="large"
																		onClick={handleGroupClick(res)}
																		data-testid="groupicon-button-icon"
																	>
																		<GroupIcon />
																	</IconButton>
																</Box>
															)}
														</Stack>
													</Stack>
												}
												details={
													<div>
														<CitationDescription
															currentNode={res}
															parentNode={res}
															{...(editPermission
																? {
																		isMultiCheckBoxRequired: true,
																		checkedCitations,
																		handleCheckBox,
																  }
																: {
																		isMultiCheckBoxRequired: false,
																  })}
														/>
													</div>
												}
											/>
										);
									},
								)}
							</Stack>
						</SelectableHOC>
					</Grid>
					{editPermission && (
						<Grid
							container
							spacing={2}
							justifyContent="flex-end"
							className={styles['inventory-submit-for-review-grid']}
						>
							<Grid item xs={12} sm={4} md={4} lg={3} xl={2}>
								<ButtonComp
									btnType="secondary"
									data-testid="btn-submit"
									clickHandler={handleSubmitClick}
									disabled={
										!regulationGroups.some((node) => node.group.length !== 0)
									}
								>
									Submit for Review
								</ButtonComp>
							</Grid>
							<Grid item xs={12} sm={4} md={2} lg={2} xl={1}>
								<ButtonComp
									btnType="secondary"
									data-testid="btn-cancel"
									clickHandler={handleCancelClick}
									disabled={
										regulationGroups.length === 0 ||
										JSON.stringify(regulationGroups) ===
											JSON.stringify(originalRegulationGroups)
									}
								>
									Cancel
								</ButtonComp>
							</Grid>
							<Grid item xs={12} sm={4} md={2} lg={2} xl={1}>
								<ButtonComp
									btnType="primary"
									data-testid="btn-save"
									clickHandler={handleSaveClick}
									compClass={styles['group-save-btn']}
									disabled={regulationGroups.length === 0}
								>
									Save
								</ButtonComp>
							</Grid>
						</Grid>
					)}
				</>
			)}
			{selectedGroup && typeof selectedGroup !== 'undefined' && anchorEl && (
				<Popper
					id="groups-popper"
					open={open}
					anchorEl={anchorEl}
					placement="bottom-end"
					data-testId="groups-popper"
					sx={{ zIndex: 100 }}
				>
					<CitationGroupList
						node={selectedGroup}
						deleteGroup={regulationGroupDelete}
						groupList={regulationGroups}
						allowEdit={editPermission}
						handleClose={handleCitationGroupClose}
					/>
				</Popper>
			)}
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
			<SideDrawer
				className={styles['groups-drawer']}
				drawerState={addGroupDrawerState}
			>
				<RegulatoryInventoryAddGroup onAddHandler={onAddHandler} />
			</SideDrawer>
			<SideDrawer
				className={styles['groups-drawer']}
				drawerState={addToGroupDrawerState}
			>
				<RegulatoryInventoryAddToGroup
					onAddHandler={onAddHandler}
					objectId={subPartId ?? ''}
				/>
			</SideDrawer>
		</Grid>
	);
};
export default GroupAssessmentDetails;
